/**
 * The SSO fixture contract, shared by the `/e2e/*` endpoints that maintain it and the Playwright
 * specs that consume it. It lives here because both must agree exactly - a second copy of the rule
 * would drift and the tests would look for an organisation or a user nobody made.
 *
 * Three standing WorkOS organisations per environment, hand-linked to the shared IdP once and never
 * re-linked by code. Domains and mutability are COMMITTED here; the only environment-specific part
 * is which organisation ids they are, in `SSO_A/B/C_ORG_ID`.
 */

import { getReviewEnvId } from '@dealstack/shared/lib/common/environment';
import { extractDomain } from '@dealstack/shared/lib/format/emailHelpers';

export type SsoFixtureLabel = 'A' | 'B' | 'C';

export interface SsoFixtureSpec {
	domain: string;
	/** Whether a spec may write this organisation's settings. Only its users are free game on A. */
	mutable: boolean;
	/** The state its GenericSAML connection is held in permanently. C is linked, never activated. */
	connection: 'active' | 'inactive';
}

/** The shared IdP every environment authenticates against. `MOCK_SAML_URL` overrides it. */
export const MOCK_SAML_URL = 'https://mock-saml.qa.dealstack-dev.com';

export const SSO_FIXTURE: Record<SsoFixtureLabel, SsoFixtureSpec> = {
	A: { domain: 'sso-a.dealstack-dev.com', mutable: false, connection: 'active' },
	B: { domain: 'sso-b.dealstack-dev.com', mutable: true, connection: 'active' },
	C: { domain: 'sso-c.dealstack-dev.com', mutable: true, connection: 'inactive' },
};

export const SSO_FIXTURE_LABELS: readonly SsoFixtureLabel[] = ['A', 'B', 'C'];

/** Derived, never a second literal: moving a domain in `SSO_FIXTURE` moves the mail rule with it. */
export const SSO_FIXTURE_DOMAINS: readonly string[] = SSO_FIXTURE_LABELS.map(
	(label) => SSO_FIXTURE[label].domain
);

const SSO_FIXTURE_DOMAINS_LOWERCASE = SSO_FIXTURE_DOMAINS.map((domain) => domain.toLowerCase());

/**
 * These domains have no MX record, so mail addressed to one is undeliverable and every non-production
 * sender must reroute it - see `getDeliverableRecipient` in `lib/format/emailHelpers`.
 */
export const isSsoFixtureDomainEmail = (email: string): boolean => {
	const domain = extractDomain(email)?.toLowerCase();

	return domain !== undefined && SSO_FIXTURE_DOMAINS_LOWERCASE.includes(domain);
};

export const SSO_FIXTURE_ORG_ID_VARIABLES: Record<SsoFixtureLabel, string> = {
	A: 'SSO_A_ORG_ID',
	B: 'SSO_B_ORG_ID',
	C: 'SSO_C_ORG_ID',
};

/**
 * The name every fixture organisation carries. `cleanWorkOs` protects organisations on this prefix,
 * so it is always built from the CONTRACT domain - a spec that moves a domain elsewhere must not
 * rename the organisation out of that protection.
 */
export const SSO_FIXTURE_NAME_PREFIX = 'SSO Fixture ';

export const ssoFixtureOrganizationName = (label: SsoFixtureLabel): string =>
	`${SSO_FIXTURE_NAME_PREFIX}${label} (${SSO_FIXTURE[label].domain})`;

/** Per-label state of one fixture organisation, as observed in WorkOS and in this database. */
export interface SsoFixtureOrganization {
	label: SsoFixtureLabel;
	/** The domain the contract assigns this label - not necessarily the one attached right now. */
	domain: string;
	mutable: boolean;
	workOsOrgId: string;
	dbOrganizationId: string;
	/** The DealStack organization row's name - what an organisation invite email names. */
	dbOrganizationName: string;
	/**
	 * The registered, active user this label owns - a member of this organisation and nothing else,
	 * since a WorkOS user may belong to one. It is what makes login routing assertable per label:
	 * `POST /auth/method` answers `WORKOS_PASSWORD` for an address it does not know, so an
	 * invented one proves nothing.
	 */
	canonicalUserEmail: string;
	/** Domains attached to the WorkOS organisation now; a mutation spec moves these about. */
	workOsDomains: string[];
	/** `null` when no connection is linked at all - which is a fixture problem, not a state. */
	connectionState: string | null;
	/** WorkOS keeps these in step with the organisation's domains, so they decide SSO routing. */
	connectionDomains: string[];
}

/** Wire shape of `GET /e2e/sso-fixture`. */
export interface SsoFixture {
	/**
	 * Fixture A's canonical user - the one every read-only spec signs in as. Already registered and
	 * active, so a login lands on the dashboard, not the register form.
	 */
	canonicalUserEmail: string;
	organizations: SsoFixtureOrganization[];
}

/** A plain record rather than `NodeJS.ProcessEnv`, so this stays free of Node types. */
export type SsoFixtureEnv = Record<string, string | undefined>;

const LOCAL = 'local';

const trimmed = (value: string | undefined): string | undefined => {
	const result = value?.trim();
	return result && result.length > 0 ? result : undefined;
};

const slug = (value: string): string =>
	value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 40);

export const isSsoFixtureLabel = (value: string): value is SsoFixtureLabel =>
	(SSO_FIXTURE_LABELS as readonly string[]).includes(value);

/**
 * Organisation ids are never defaulted or discovered: resolution is a pinned-id read, so it cannot
 * create anything, cannot pick "the oldest of several", and stays correct after a spec has moved a
 * domain off the organisation - which is exactly when `listOrganizations({ domains })` stops
 * finding it.
 */
export const resolveSsoFixtureOrgIds = (
	env: SsoFixtureEnv
): Partial<Record<SsoFixtureLabel, string>> => {
	const orgIds: Partial<Record<SsoFixtureLabel, string>> = {};
	for (const label of SSO_FIXTURE_LABELS) {
		const orgId = trimmed(env[SSO_FIXTURE_ORG_ID_VARIABLES[label]]);
		if (orgId) {
			orgIds[label] = orgId;
		}
	}
	return orgIds;
};

export const resolveMockSamlUrl = (env: SsoFixtureEnv): string =>
	(trimmed(env['MOCK_SAML_URL']) ?? MOCK_SAML_URL).replace(/\/+$/, '');

/**
 * One canonical SSO user per environment, so each label's login routing is unambiguous - a fixture
 * convention, not a WorkOS constraint (a user may belong to many organisations): review env ->
 * `pr-<N>`, QA/dev -> the environment name, an ephemeral stack or a laptop -> its seed identity.
 *
 * `E2E_TEST_LOGIN` is the same string an isolated CI stack seeds with - its entrypoint runs
 * `email="$E2E_TEST_LOGIN" prisma db seed` but does not export `email` to the serving backend, and
 * it reports itself as `local`. Reading both makes the endpoint derive the identity the seed used
 * instead of refusing to derive one at all.
 */
export const deriveSsoFixtureEnvId = (env: SsoFixtureEnv): string | undefined => {
	const apiFrontendUrl = trimmed(env['BE_APPLICATION_API_FRONTEND_URL']);
	const reviewEnvId = apiFrontendUrl ? getReviewEnvId(apiFrontendUrl) : undefined;
	if (reviewEnvId) {
		return reviewEnvId;
	}

	const backendEnv = trimmed(env['BE_APPLICATION_ENVIRONMENT']) ?? trimmed(env['NODE_ENV']);
	if (backendEnv && backendEnv !== LOCAL) {
		return slug(backendEnv);
	}

	const seedIdentity = trimmed(env['email']) ?? trimmed(env['E2E_TEST_LOGIN']);
	const [localPart] = seedIdentity?.split('@') ?? [];
	return localPart ? `local-${slug(localPart)}` : undefined;
};

export const canonicalSsoUserEmail = (domain: string, envId: string): string =>
	`ssotest+${envId}@${domain}`;

/** Marks an address `buildSsoAddress` generated, so the fixture prune can tell it from a person's. */
export const SSO_TEST_INVITEE_TAG = 'e2e';

// Invitees from before the tag: a known e2e local part plus the run suffix (a run id or epoch
// millis, then 5 random characters). The length floor spares hand-made invites like `alice+qa1`.
const LEGACY_TEST_INVITEE =
	/^(alice|joiner|dirjoiner|dirleaver|dirmover[a-z0-9]{4})\+[a-z0-9]{12,}$/;
const TAGGED_TEST_INVITEE = new RegExp(`^[a-z0-9]+\\+${SSO_TEST_INVITEE_TAG}[a-z0-9]+$`);

/**
 * Whether an address is an e2e-generated invitee on a fixture domain. Never true of a canonical
 * `ssotest+` user, which every environment signs in as.
 */
export const isSsoTestInviteeEmail = (email: string): boolean => {
	if (!isSsoFixtureDomainEmail(email)) {
		return false;
	}
	const localPart = email.slice(0, email.lastIndexOf('@')).toLowerCase();

	return (
		!localPart.startsWith('ssotest+') &&
		(TAGGED_TEST_INVITEE.test(localPart) || LEGACY_TEST_INVITEE.test(localPart))
	);
};
